import React, { useState } from 'react';
import { X, Sparkles, RefreshCw, Download, Save, Share2, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toShareUrl } from '../../utils/helpers';
import { academyAPI } from '../../services/api';
import ShareModal from '../../components/common/ShareModal';
import toast from 'react-hot-toast';

export default function AiFlyerModal({ isOpen, onClose, program, academyId, onFlyerGenerated }) {
  const { theme } = useTheme();
  const [state, setState] = useState('idle'); // idle | generating | preview | saving | saved | error
  const [flyerHtml, setFlyerHtml] = useState(null);
  const [flyerUrl, setFlyerUrl] = useState(null);
  const [showShare, setShowShare] = useState(false);

  if (!isOpen) return null;

  const generate = async () => {
    setState('generating');
    setFlyerHtml(null);
    setFlyerUrl(null);
    try {
      const result = await academyAPI.programs.generateFlyer(academyId, program.id);
      setFlyerHtml(result.html);
      setState('preview');
    } catch {
      setState('error');
      toast.error('Failed to generate flyer. Check that OPENAI_API_KEY is set.');
    }
  };

  const save = async () => {
    setState('saving');
    try {
      const result = await academyAPI.programs.saveFlyer(academyId, program.id);
      setFlyerUrl(result.flyerUrl);
      setState('saved');
      if (onFlyerGenerated) onFlyerGenerated(program.id, result.flyerUrl);
      toast.success('Flyer linked to program!');
    } catch {
      setState('preview');
      toast.error('Failed to save flyer.');
    }
  };

  const downloadHtml = () => {
    const blob = new Blob([flyerHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flyer-${program.name.toLowerCase().replace(/\s+/g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // toShareUrl, not origin + value: see helpers.js — concatenating blindly
  // doubled the domain whenever flyerUrl came back absolute.
  const flyerShareUrl = flyerUrl ? toShareUrl(flyerUrl) : null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      >
        <div className={`${theme.card} rounded-2xl w-full max-w-3xl max-h-[95vh] flex flex-col shadow-2xl`}>

          {/* Header */}
          <div className={`flex items-center justify-between px-6 py-4 border-b ${theme.border} shrink-0`}>
            <div>
              <h3 className={`text-lg font-bold ${theme.text} flex items-center gap-2`}>
                <Sparkles size={18} className="text-purple-500" />
                AI Flyer Generator
              </h3>
              <p className={`text-xs ${theme.textMuted} mt-0.5`}>{program.name}</p>
            </div>
            <button onClick={onClose} className={`p-2 rounded-full ${theme.cardHover} transition`} aria-label="Close">
              <X size={18} className={theme.textMuted} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5 min-h-0">

            {/* Idle */}
            {state === 'idle' && (
              <div className="flex flex-col items-center justify-center py-16 gap-5">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                  <Sparkles size={36} className="text-white" />
                </div>
                <div className="text-center">
                  <p className={`text-lg font-bold ${theme.text} mb-2`}>Generate an AI-Powered Flyer</p>
                  <p className={`text-sm ${theme.textSecondary} max-w-sm`}>
                    An AI image will be created and built into a self-contained, SEO-friendly HTML flyer
                    for <strong>{program.name}</strong>. Review it, then save to link it to the program.
                  </p>
                </div>
                <button
                  onClick={generate}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition shadow-lg"
                >
                  <Sparkles size={16} /> Generate Flyer
                </button>
              </div>
            )}

            {/* Generating */}
            {state === 'generating' && (
              <div className="flex flex-col items-center justify-center py-20 gap-5">
                <div className="w-16 h-16 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
                <div className="text-center">
                  <p className={`font-semibold ${theme.text} mb-1`}>Generating your flyer…</p>
                  <p className={`text-sm ${theme.textMuted}`}>AI is creating a custom image. This takes ~15–30 seconds.</p>
                </div>
              </div>
            )}

            {/* Error */}
            {state === 'error' && (
              <div className="flex flex-col items-center justify-center py-16 gap-5">
                <div className="text-center p-6 rounded-xl border border-red-200 bg-red-50 max-w-sm">
                  <p className="text-red-700 font-semibold mb-1">Generation failed</p>
                  <p className="text-red-500 text-sm">Make sure OPENAI_API_KEY is set in your .env file and try again.</p>
                </div>
                <button
                  onClick={generate}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition"
                >
                  <RefreshCw size={15} /> Try Again
                </button>
              </div>
            )}

            {/* Preview / Saved */}
            {(state === 'preview' || state === 'saving' || state === 'saved') && flyerHtml && (
              <div className="flex flex-col gap-4">
                {/* Action bar */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    {state === 'saved' ? (
                      <p className={`text-sm font-semibold text-green-600 flex items-center gap-1.5`}>
                        <CheckCircle size={15} /> Flyer saved and linked to program
                      </p>
                    ) : (
                      <p className={`text-sm font-semibold ${theme.text}`}>Preview — happy with it? Click Save to link to program.</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={downloadHtml}
                      className={`flex items-center gap-1.5 px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.text} ${theme.cardHover} transition`}
                    >
                      <Download size={14} /> Download
                    </button>
                    <button
                      onClick={generate}
                      className="flex items-center gap-1.5 px-3 py-2 border border-purple-300 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-50 transition"
                    >
                      <RefreshCw size={14} /> Regenerate
                    </button>
                    {state === 'saved' && flyerUrl && (
                      <button
                        onClick={() => setShowShare(true)}
                        className="flex items-center gap-1.5 px-3 py-2 border border-blue-300 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition"
                      >
                        <Share2 size={14} /> Share
                      </button>
                    )}
                    {(state === 'preview' || state === 'saving') && (
                      <button
                        onClick={save}
                        disabled={state === 'saving'}
                        className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition disabled:opacity-60"
                      >
                        <Save size={14} />
                        {state === 'saving' ? 'Saving…' : 'Save Flyer'}
                      </button>
                    )}
                  </div>
                </div>

                {/* iframe preview */}
                <div className={`rounded-xl overflow-hidden border ${theme.border} shadow-sm`} style={{ height: '540px' }}>
                  <iframe
                    srcDoc={flyerHtml}
                    title="Flyer Preview"
                    className="w-full h-full"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share modal */}
      {showShare && flyerShareUrl && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          entity={{ id: program.id, name: program.name, description: `View the flyer for ${program.name}` }}
          entityType="flyer"
          urlPattern={flyerShareUrl.replace(program.id, '{id}')}
          currentUser={null}
        />
      )}
    </>
  );
}
